import cn from 'classnames'
import type { ChangeEvent, FC } from 'react'

import { PartnerSummaryBlock } from 'clickNgo/entities/partner/ui/partnerSummaryBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { SimpleTable } from 'cng-common/components/simpleTable'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'

import * as TRANSLATION_KEYS from '../../../widgets/devicesInfo/partnerDevicesInfo/translations'
import { formatDisplayNumber } from '../lib/formatters'
import type { PartnerDevicesInfoPartnerSummary } from '../model/types'
import type { PartnerDevicesInfoFeatureProps } from '../model/types'
import { usePartnerDevicesInfo } from '../model/usePartnerDevicesInfo'

import { OverLastYearsSelector } from './parts/overLastYearsSelector/OverLastYearsSelector'

import styles from './PartnerDevicesInfoFeature.module.scss'

const INPUT_FIELDS = {
  a3AllBrands: 'a3AllBrands',
  a3HpBrand: 'a3HpBrand',
  a4AllBrands: 'a4AllBrands',
  a4HpBrand: 'a4HpBrand',
} as const

export const PartnerDevicesInfoFeature: FC<PartnerDevicesInfoFeatureProps> = ({ ...props }) => (
  <PartnerDevicesInfoFeatureContent {...props} />
)

type PartnerDevicesInfoFeatureContentProps = PartnerDevicesInfoFeatureProps & {
  partnerSummary?: PartnerDevicesInfoPartnerSummary | null
  onClose?: () => void
  onConfirmed?: () => void | Promise<void>
}

export const PartnerDevicesInfoFeatureWithSummary: FC<PartnerDevicesInfoFeatureContentProps> = (
  props
) => <PartnerDevicesInfoFeatureContent {...props} />

const PartnerDevicesInfoFeatureContent: FC<PartnerDevicesInfoFeatureContentProps> = (props) => {
  const { partnerId, isEditable, fYear, partnerSummary = null, onClose, onConfirmed } = props
  const {
    resource,
    draft,
    overLastYearsValue,
    isLoading,
    isSaving,
    isA3ContractualUnitsAdded,
    isA4ContractualUnitsAdded,
    loadError,
    saveError,
    isDirty,
    parsedValues,
    percentValues,
    validation,
    loadData,
    handleFieldChange,
    handleOverLastYearsSelect,
    handleReset,
    handleSave,
    handleAddA3ContractualUnits,
    handleAddA4ContractualUnits,
  } = usePartnerDevicesInfo({
    partnerId,
    fYear,
  })

  const handleInputChange =
    (field: keyof typeof INPUT_FIELDS) => (event: ChangeEvent<HTMLInputElement>) => {
      handleFieldChange(INPUT_FIELDS[field], event.target.value)
    }

  const handleSaveClick = async () => {
    const isSaved = await handleSave()

    if (!isSaved) {
      return
    }

    onClose?.()
    await onConfirmed?.()
  }

  if (isLoading) {
    return (
      <section className={styles.root}>
        <div className={styles.card}>
          <div className={styles.loaderWrap}>
            <LoaderOverlay isVisible color="white" />
          </div>
        </div>
      </section>
    )
  }

  if (loadError || !resource || !draft || !parsedValues || !percentValues || !validation) {
    return (
      <section className={styles.root}>
        <div className={styles.card}>
          <div className={styles.errorState}>
            <p className={styles.errorMessage}>{loadError ?? 'No data available.'}</p>
            <Button className={styles.secondaryButton} onClick={() => void loadData()} size="sm">
              Retry
            </Button>
          </div>
        </div>
      </section>
    )
  }

  const totalContractualSales =
    resource.data.phyContractualUnitsA3 + resource.data.phyContractualUnitsA4

  return (
    <section className={styles.root}>
      <div className={styles.card}>
        <div className={styles.controlsRow}>
          {partnerSummary ? <PartnerSummaryBlock {...partnerSummary} /> : null}
          <div className={styles.autoCalculateWrap}>
            <OverLastYearsSelector
              value={overLastYearsValue}
              isDisabled={!isEditable || isSaving}
              onSelect={(value) => void handleOverLastYearsSelect(value)}
            />
          </div>
        </div>
        {saveError ? <div className={styles.feedbackError}>{saveError}</div> : null}

        <div className={styles.tableShell}>
          <SimpleTable className={styles.table}>
            <SimpleTable.HeaderRow className={styles.headerRow}>
              <SimpleTable.HeaderCell className={styles.metricHeaderCell} />
              <SimpleTable.HeaderCell className={styles.headerCell}>
                <Translation text={TRANSLATION_KEYS.formFields.allBrandsTitle} span />
              </SimpleTable.HeaderCell>
              <SimpleTable.HeaderCell className={styles.headerCell}>
                <Translation text={TRANSLATION_KEYS.formFields.hpBrandTitle} span />
              </SimpleTable.HeaderCell>
              <SimpleTable.HeaderCell className={styles.headerCell}>
                <Translation text={TRANSLATION_KEYS.formFields.hpShareTitle} span />
              </SimpleTable.HeaderCell>
              <SimpleTable.HeaderCell className={styles.headerCell}>
                <Translation
                  text={TRANSLATION_KEYS.formFields.partnerContractualSalesHyTitle}
                  span
                />
              </SimpleTable.HeaderCell>
            </SimpleTable.HeaderRow>

            <SimpleTable.BodyRow className={styles.bodyRow}>
              <SimpleTable.HeaderCell className={styles.metricCell}>
                <span className={styles.metricText}>
                  <Translation text={TRANSLATION_KEYS.rowLabels.a3DevicesLabel} span />
                </span>
              </SimpleTable.HeaderCell>
              <SimpleTable.BodyCell className={styles.valueCell}>
                {isEditable ? (
                  <input
                    className={cn(styles.input, {
                      [styles.inputInvalid]: !validation.a3AllBrands,
                    })}
                    value={draft.a3AllBrands}
                    disabled={isSaving}
                    onChange={handleInputChange('a3AllBrands')}
                    inputMode="decimal"
                  />
                ) : (
                  formatDisplayNumber(parsedValues.a3AllBrands)
                )}
              </SimpleTable.BodyCell>
              <SimpleTable.BodyCell className={styles.valueCell}>
                {isEditable ? (
                  <input
                    className={cn(styles.input, {
                      [styles.inputInvalid]: !validation.a3HpBrand || !validation.a3PercentValid,
                    })}
                    value={draft.a3HpBrand}
                    disabled={isSaving}
                    onChange={handleInputChange('a3HpBrand')}
                    inputMode="decimal"
                  />
                ) : (
                  formatDisplayNumber(parsedValues.a3HpBrand)
                )}
              </SimpleTable.BodyCell>
              <SimpleTable.BodyCell
                className={cn(styles.valueCell, {
                  [styles.valueCellInvalid]: !validation.a3PercentValid,
                })}
              >
                {percentValues.a3}
              </SimpleTable.BodyCell>
              <SimpleTable.BodyCell className={styles.contractualCell}>
                <div className={styles.contractualCellContent}>
                  <span className={styles.contractualValue}>
                    {formatDisplayNumber(resource.data.phyContractualUnitsA3)}
                  </span>
                  {isEditable ? (
                    <Button
                      className={styles.inlineAction}
                      disabled={isSaving || isA3ContractualUnitsAdded}
                      onClick={handleAddA3ContractualUnits}
                      variant="text"
                      size="sm"
                    >
                      Add
                    </Button>
                  ) : null}
                </div>
              </SimpleTable.BodyCell>
            </SimpleTable.BodyRow>

            <SimpleTable.BodyRow className={cn(styles.bodyRow, styles.bodyRowAlt)}>
              <SimpleTable.HeaderCell className={styles.metricCell}>
                <span className={styles.metricText}>
                  <Translation text={TRANSLATION_KEYS.rowLabels.a4DevicesLabel} span />
                </span>
              </SimpleTable.HeaderCell>
              <SimpleTable.BodyCell className={styles.valueCell}>
                {isEditable ? (
                  <input
                    className={cn(styles.input, {
                      [styles.inputInvalid]: !validation.a4AllBrands,
                    })}
                    value={draft.a4AllBrands}
                    disabled={isSaving}
                    onChange={handleInputChange('a4AllBrands')}
                    inputMode="decimal"
                  />
                ) : (
                  formatDisplayNumber(parsedValues.a4AllBrands)
                )}
              </SimpleTable.BodyCell>
              <SimpleTable.BodyCell className={styles.valueCell}>
                {isEditable ? (
                  <input
                    className={cn(styles.input, {
                      [styles.inputInvalid]: !validation.a4HpBrand || !validation.a4PercentValid,
                    })}
                    value={draft.a4HpBrand}
                    disabled={isSaving}
                    onChange={handleInputChange('a4HpBrand')}
                    inputMode="decimal"
                  />
                ) : (
                  formatDisplayNumber(parsedValues.a4HpBrand)
                )}
              </SimpleTable.BodyCell>
              <SimpleTable.BodyCell
                className={cn(styles.valueCell, {
                  [styles.valueCellInvalid]: !validation.a4PercentValid,
                })}
              >
                {percentValues.a4}
              </SimpleTable.BodyCell>
              <SimpleTable.BodyCell className={styles.contractualCell}>
                <div className={styles.contractualCellContent}>
                  <span className={styles.contractualValue}>
                    {formatDisplayNumber(resource.data.phyContractualUnitsA4)}
                  </span>
                  {isEditable ? (
                    <Button
                      className={styles.inlineAction}
                      disabled={isSaving || isA4ContractualUnitsAdded}
                      onClick={handleAddA4ContractualUnits}
                      variant="text"
                      size="sm"
                    >
                      Add
                    </Button>
                  ) : null}
                </div>
              </SimpleTable.BodyCell>
            </SimpleTable.BodyRow>

            <SimpleTable.BodyRow className={styles.footerRow}>
              <SimpleTable.HeaderCell className={styles.metricCell}>
                <span className={styles.metricTextStrong}>
                  <Translation text={TRANSLATION_KEYS.rowLabels.totalMIFLabel} span />
                </span>
              </SimpleTable.HeaderCell>
              <SimpleTable.BodyCell className={styles.valueCellStrong}>
                {formatDisplayNumber(parsedValues.totalAllBrands)}
              </SimpleTable.BodyCell>
              <SimpleTable.BodyCell className={styles.valueCellStrong}>
                {formatDisplayNumber(parsedValues.totalHpBrand)}
              </SimpleTable.BodyCell>
              <SimpleTable.BodyCell
                className={cn(styles.valueCellStrong, {
                  [styles.valueCellInvalid]: !validation.totalPercentValid,
                })}
              >
                {percentValues.total}
              </SimpleTable.BodyCell>
              <SimpleTable.BodyCell className={styles.contractualCellStrong}>
                {formatDisplayNumber(totalContractualSales)}
              </SimpleTable.BodyCell>
            </SimpleTable.BodyRow>
          </SimpleTable>
        </div>

        {isEditable ? (
          <footer className={styles.footer}>
            <Button
              className={styles.secondaryButton}
              disabled={!isDirty || isSaving}
              onClick={handleReset}
              variant="outlined"
              size="sm"
            >
              Reset
            </Button>
            <Button
              className={styles.primaryButton}
              disabled={!validation.canSave || isSaving}
              onClick={() => void handleSaveClick()}
              size="sm"
              loading={isSaving}
            >
              <Translation
                text={TRANSLATION_KEYS.buttons[isDirty ? 'mifReviewAndUpdate' : 'mifReview']}
                span
              />
            </Button>
          </footer>
        ) : null}
      </div>
    </section>
  )
}
